import { DeleteOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Input, InputNumber, Row, Select, TimePicker } from "antd";
import { useGetCountriesQuery } from "api/TenantSlug/Country/hooks";
import { useBaseFilter, FilterColumn } from "components/UI/BaseFilter/hooks/useBaseFilter.ts";
import { FilterRule, FilterType } from "components/UI/BaseFilter/types/filter";
import { useConversionTypeSelectOptions } from "components/UI/Selects/ConversionTypeSelectSearchable/useConversionTypeSelectOptions.ts";
import { useSourceSelectOptions } from "components/UI/Selects/SourceSelectSearchable/useSourceSelectOptions.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import dayjs from "dayjs";
import { useFilterTranslations } from "hooks/useFilterTranslations";
import { DATE_FORMAT, DATE_FORMAT_WITH_TIME_AND_SECONDS } from "localConstants";
import { SearchFilterEnum } from "types/enums";
import { handleShowSearchFilterOption } from "utils/handleShowSearchFilterOption";

import OfferData = App.Data.OfferData;

const { RangePicker } = DatePicker;

interface BaseFilterItemProps {
    filter: FilterRule;
    offer?: OfferData;
    cascade?: { id: string };
    availableFilterColumns: FilterColumn[];
    groupId?: number;
}

export const BaseFilterItem = ({ availableFilterColumns, filter, offer, cascade, groupId }: BaseFilterItemProps) => {
    const { type } = filter.filterType;
    const baseFilterUtils = useBaseFilter(offer);
    const filterTranslations = useFilterTranslations();

    const updateFilter = (id: number, key: keyof FilterRule, value: unknown) => {
        if (cascade) {
            if (groupId !== undefined) {
                baseFilterUtils.updateCascadeFilterInGroup(cascade.id, groupId, id, key, value);
            } else {
                baseFilterUtils.updateCascadeFilter(cascade.id, id, key, value);
            }
        } else if (offer) {
            if (groupId !== undefined) {
                baseFilterUtils.updateFilterInGroup(offer, groupId, id, key, value);
            } else {
                baseFilterUtils.updateFilter(offer, id, key, value);
            }
        }
    };

    const removeFilter = (id: number) => {
        if (cascade) {
            if (groupId !== undefined) {
                baseFilterUtils.removeCascadeFilterFromGroup(cascade.id, groupId, id);
            } else {
                baseFilterUtils.removeCascadeFilter(cascade.id, id);
            }
        } else if (offer) {
            if (groupId !== undefined) {
                baseFilterUtils.removeFilterFromGroup(offer, groupId, id);
            } else {
                baseFilterUtils.removeFilter(offer, id);
            }
        }
    };

    const getCountriesQuery = useGetCountriesQuery();

    const isConversionTypeFilter =
        type === FILTER_TYPES.conversionStateTypeSelect.type ||
        type === FILTER_TYPES.conversionStateTypeMultiSelect.type;

    const { options: conversionSourceOptions } = useSourceSelectOptions();
    const { options: conversionTypeOptions } = useConversionTypeSelectOptions(isConversionTypeFilter);

    const placeholder = (filter.filterType as FilterType).placeholder ?? filterTranslations.placeholders[type];

    const renderValueInput = () => {
        const isRange = filter.operator === SearchFilterEnum.Between || filter.operator === SearchFilterEnum.NotBetween;

        if (isRange) {
            const [start, end] = Array.isArray(filter.value) ? filter.value : [null, null];
            return (
                <RangePicker
                    className="w-full"
                    showTime={type === "datetime"}
                    placeholder={[filterTranslations.options.from, filterTranslations.options.to]}
                    value={[start ? dayjs(start as string) : null, end ? dayjs(end as string) : null]}
                    onChange={(dates) => {
                        const [s, e] = dates ?? [null, null];
                        const fmt = (d: dayjs.Dayjs | null) => (d ? d.format(DATE_FORMAT_WITH_TIME_AND_SECONDS) : null);
                        // if it's a pure date filter, end should be endOf("day")
                        const endValue =
                            type === FILTER_TYPES.date.type && e
                                ? e.endOf("day").format(DATE_FORMAT_WITH_TIME_AND_SECONDS)
                                : fmt(e);
                        updateFilter(filter.id, "value", [fmt(s), endValue]);
                    }}
                    allowClear
                />
            );
        }

        switch (type) {
            case FILTER_TYPES.string.type:
                return (
                    <Input
                        placeholder={placeholder}
                        value={filter.value as string}
                        onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
                    />
                );

            case FILTER_TYPES.number.type:
                return (
                    <InputNumber
                        style={{ width: "100%" }}
                        placeholder={placeholder}
                        value={filter.value as number}
                        onChange={(v: number | null) => updateFilter(filter.id, "value", v ?? 0)}
                    />
                );

            case FILTER_TYPES.date.type:
                return (
                    <DatePicker
                        placeholder={placeholder}
                        className="w-full"
                        value={filter.value ? dayjs(filter.value as string) : undefined}
                        onChange={(d) => updateFilter(filter.id, "value", d?.format(DATE_FORMAT) ?? null)}
                    />
                );

            case FILTER_TYPES.datetime.type:
                return (
                    <DatePicker
                        showTime
                        placeholder={placeholder}
                        value={filter.value ? dayjs(filter.value as string) : undefined}
                        onChange={(d) =>
                            updateFilter(filter.id, "value", d?.format(DATE_FORMAT_WITH_TIME_AND_SECONDS) ?? null)
                        }
                        className="w-full"
                    />
                );

            case FILTER_TYPES.select.type:
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={filter.filterOptions || []}
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.boolean.type:
                return (
                    <Select
                        allowClear
                        placeholder={placeholder}
                        options={[
                            { label: filterTranslations.options.yes, value: true },
                            { label: filterTranslations.options.no, value: false },
                        ]}
                        value={filter.value as boolean | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            // custom select filters
            case FILTER_TYPES.countrySelect.type:
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={
                            getCountriesQuery.data?.countries?.map((country) => ({
                                label: country.name,
                                value: country.name,
                            })) || []
                        }
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.conversionStateSourceSelect.type:
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionSourceOptions}
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.conversionStateTypeSelect.type:
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionTypeOptions || []}
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            // custom multiselect filters
            case FILTER_TYPES.countryMultiSelect.type:
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={
                            getCountriesQuery.data?.countries?.map((country) => ({
                                label: country.name,
                                value: country.name,
                            })) || []
                        }
                        value={filter.value as unknown[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.conversionStateSourceMultiSelect.type:
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionSourceOptions}
                        value={filter.value as unknown[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.conversionStateTypeMultiSelect.type:
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionTypeOptions || []}
                        value={filter.value as unknown[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            // New filter types
            case FILTER_TYPES.ratio.type: {
                const ratioValue = (filter.value as { field1: string; field2: string; percentage: number }) || {
                    field1: "",
                    field2: "",
                    percentage: 0,
                };
                return (
                    <div className="space-y-2">
                        <Select
                            placeholder={filterTranslations.placeholders.first_field}
                            options={filter.filterOptions || []}
                            value={ratioValue.field1}
                            onChange={(v) => updateFilter(filter.id, "value", { ...ratioValue, field1: v })}
                            className="w-full"
                        />
                        <Select
                            placeholder={filterTranslations.placeholders.second_field}
                            options={filter.filterOptions || []}
                            value={ratioValue.field2}
                            onChange={(v) => updateFilter(filter.id, "value", { ...ratioValue, field2: v })}
                            className="w-full"
                        />
                        <InputNumber
                            placeholder={filterTranslations.placeholders.percentage}
                            min={0}
                            max={100}
                            value={ratioValue.percentage}
                            onChange={(v) => updateFilter(filter.id, "value", { ...ratioValue, percentage: v || 0 })}
                            className="w-full"
                            addonAfter="%"
                        />
                    </div>
                );
            }

            case FILTER_TYPES.history_sent.type:
            case FILTER_TYPES.history_rejected.type:
            case FILTER_TYPES.history_accepted.type: {
                const historyValue = (filter.value as { timePeriod: string }) || { timePeriod: "7_days" };
                return (
                    <Select
                        placeholder={filterTranslations.placeholders.time_period}
                        options={filter.filterOptions || []}
                        value={historyValue.timePeriod}
                        onChange={(v) => updateFilter(filter.id, "value", { ...historyValue, timePeriod: v })}
                        className="w-full"
                    />
                );
            }

            case FILTER_TYPES.history_rejected_reason.type: {
                const rejectedReasonValue = (filter.value as { timePeriod: string; reason: string }) || {
                    timePeriod: "7_days",
                    reason: "",
                };
                return (
                    <div className="space-y-2">
                        <Select
                            placeholder={filterTranslations.placeholders.time_period}
                            options={filter.filterOptions || []}
                            value={rejectedReasonValue.timePeriod}
                            onChange={(v) =>
                                updateFilter(filter.id, "value", { ...rejectedReasonValue, timePeriod: v })
                            }
                            className="w-full"
                        />
                        <Input
                            placeholder={filterTranslations.placeholders.rejection_reason}
                            value={rejectedReasonValue.reason}
                            onChange={(e) =>
                                updateFilter(filter.id, "value", { ...rejectedReasonValue, reason: e.target.value })
                            }
                            className="w-full"
                        />
                    </div>
                );
            }

            case FILTER_TYPES.external_api.type: {
                const apiValue = (filter.value as {
                    url: string;
                    method: string;
                    expectedStatus: number;
                    timeout: number;
                }) || { url: "", method: "GET", expectedStatus: 200, timeout: 5000 };
                return (
                    <div className="space-y-2">
                        <Input
                            placeholder={filterTranslations.placeholders.url_address}
                            value={apiValue.url}
                            onChange={(e) => updateFilter(filter.id, "value", { ...apiValue, url: e.target.value })}
                            className="w-full"
                        />
                        <Select
                            placeholder={filterTranslations.placeholders.http_method}
                            options={filter.filterOptions || []}
                            value={apiValue.method}
                            onChange={(v) => updateFilter(filter.id, "value", { ...apiValue, method: v })}
                            className="w-full"
                        />
                        <InputNumber
                            placeholder={filterTranslations.placeholders.expected_status}
                            min={100}
                            max={599}
                            value={apiValue.expectedStatus}
                            onChange={(v) =>
                                updateFilter(filter.id, "value", { ...apiValue, expectedStatus: v || 200 })
                            }
                            className="w-full"
                        />
                        <InputNumber
                            placeholder={filterTranslations.placeholders.timeout_ms}
                            min={1000}
                            max={30000}
                            value={apiValue.timeout}
                            onChange={(v) => updateFilter(filter.id, "value", { ...apiValue, timeout: v || 5000 })}
                            className="w-full"
                        />
                    </div>
                );
            }

            case FILTER_TYPES.time_restriction.type: {
                const timeValue = (filter.value as {
                    restrictionType: string;
                    excludedWeekdays: string[];
                    timeRanges: Array<{ start: string; end: string }>;
                }) || {
                    restrictionType: "weekday",
                    excludedWeekdays: [],
                    timeRanges: [{ start: "09:00", end: "17:00" }],
                };
                return (
                    <div className="space-y-2">
                        <Select
                            mode="multiple"
                            placeholder={filterTranslations.placeholders.excluded_weekdays}
                            options={filter.filterOptions || []}
                            value={timeValue.excludedWeekdays}
                            onChange={(v) => updateFilter(filter.id, "value", { ...timeValue, excludedWeekdays: v })}
                            className="w-full"
                            maxTagCount="responsive"
                        />
                        <div className="flex gap-2">
                            <TimePicker
                                placeholder={filterTranslations.options.from}
                                value={
                                    timeValue.timeRanges[0]?.start
                                        ? dayjs(timeValue.timeRanges[0].start, "HH:mm")
                                        : null
                                }
                                onChange={(time) => {
                                    const newRanges = [...timeValue.timeRanges];
                                    newRanges[0] = { ...newRanges[0], start: time?.format("HH:mm") || "09:00" };
                                    updateFilter(filter.id, "value", { ...timeValue, timeRanges: newRanges });
                                }}
                                format="HH:mm"
                                className="flex-1"
                            />
                            <TimePicker
                                placeholder={filterTranslations.options.to}
                                value={
                                    timeValue.timeRanges[0]?.end ? dayjs(timeValue.timeRanges[0].end, "HH:mm") : null
                                }
                                onChange={(time) => {
                                    const newRanges = [...timeValue.timeRanges];
                                    newRanges[0] = { ...newRanges[0], end: time?.format("HH:mm") || "17:00" };
                                    updateFilter(filter.id, "value", { ...timeValue, timeRanges: newRanges });
                                }}
                                format="HH:mm"
                                className="flex-1"
                            />
                        </div>
                    </div>
                );
            }

            case FILTER_TYPES.lead_count_limit.type: {
                const leadCountValue = (filter.value as { count: number; period: string }) || {
                    count: 10,
                    period: "day",
                };
                return (
                    <div className="space-y-2">
                        <InputNumber
                            placeholder={filterTranslations.placeholders.count}
                            min={1}
                            value={leadCountValue.count}
                            onChange={(v) => updateFilter(filter.id, "value", { ...leadCountValue, count: v || 1 })}
                            className="w-full"
                        />
                        <Select
                            placeholder={filterTranslations.placeholders.period}
                            options={filter.filterOptions || []}
                            value={leadCountValue.period}
                            onChange={(v) => updateFilter(filter.id, "value", { ...leadCountValue, period: v })}
                            className="w-full"
                        />
                    </div>
                );
            }

            default:
                return null;
        }
    };

    return (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={24} md={6}>
                <Select
                    className="w-full min-w-0"
                    options={availableFilterColumns.map((col) => ({ label: col.title, value: col.dataIndex }))}
                    value={filter.dataIndex}
                    onChange={(v) => updateFilter(filter.id, "dataIndex", v)}
                />
            </Col>

            <Col xs={24} md={6}>
                <Select
                    className="w-full min-w-0"
                    showSearch
                    filterOption={handleShowSearchFilterOption}
                    options={filter.filterType.operators.map((op) => ({
                        label: filterTranslations.operators[op] ?? op,
                        value: op,
                    }))}
                    value={filter.operator}
                    onChange={(v) => {
                        updateFilter(filter.id, "operator", v);
                        const isRange = v === SearchFilterEnum.Between || v === SearchFilterEnum.NotBetween;
                        let newVal: unknown;
                        if (isRange) newVal = [null, null];
                        else if (filter.filterType.type === FILTER_TYPES.number.type) newVal = 0;
                        else if (filter.filterType.type === FILTER_TYPES.boolean.type) newVal = undefined;
                        else if (filter.filterType.type === FILTER_TYPES.string.type) newVal = "";
                        else newVal = null;
                        updateFilter(filter.id, "value", newVal);
                    }}
                />
            </Col>

            <Col xs={24} md={12}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* ensure inner controls use className="w-full min-w-0" */}
                        {renderValueInput()}
                    </div>
                    <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeFilter(filter.id)}
                        aria-label="Remove filter"
                    />
                </div>
            </Col>
        </Row>
    );
};
